import { createClient } from '@supabase/supabase-js'

/**
 * The one Supabase client this app has. There is no server — PLAN.md §4
 * removed it — so this anon key, and whatever RLS policy exists on each
 * table, are the *entire* security boundary between a signed-in user and
 * every other paramedic's data. The anon key shipping to the browser is not
 * a leak; that's how Supabase's anon-key model is meant to work. What makes
 * it safe is that every table is deny-by-default from the moment it's
 * created (Phase 2 onward) — a new table with no policy is a hole, not a
 * to-do. If you're reading this file wondering where the security lives,
 * it lives in Postgres row-level security, not here.
 *
 * ---
 *
 * Why the checks below throw at *module load* rather than waiting for the
 * first query to fail:
 *
 * `import.meta.env.VITE_*` is inlined by Vite at **build** time, not read
 * at runtime. A production build with these variables unset does not fail
 * the build — it succeeds, and ships a bundle with the literal value
 * `undefined` baked into `createClient(undefined, undefined)`. That bundle
 * deploys cleanly and then breaks on the very first query, with an error
 * thrown from inside the Supabase SDK that has nothing in it pointing back
 * to "an env var was never set." Throwing here, by variable name, as soon
 * as this module is first imported (which happens the moment any screen
 * needs the client) turns that into an obvious, immediate startup failure
 * instead of a confusing one discovered later by a user. Task 6 adds a CI
 * check that catches the same failure from the other direction — before a
 * build with missing vars ever ships at all.
 */

const rawUrl = import.meta.env.VITE_SUPABASE_URL
const rawAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

const missing: string[] = []
// `!value`, not `value === undefined`: an empty string is falsy but not
// `undefined`, and an env var set to `""` is exactly as useless to
// `createClient` as one that was never set — both count as missing here.
if (!rawUrl) missing.push('VITE_SUPABASE_URL')
if (!rawAnonKey) missing.push('VITE_SUPABASE_ANON_KEY')

if (missing.length > 0) {
  throw new Error(
    `Missing required environment variable${missing.length > 1 ? 's' : ''}: ` +
      `${missing.join(', ')}. Copy .env.example to .env and fill in the real values from the ` +
      `Supabase project dashboard (Project Settings → API).`,
  )
}

/**
 * The app's anon Supabase client. Named export, wired but unused until
 * Phase 2 — no auth calls, no queries, no generated database types belong
 * here. This module's entire job is existing safely: importing it either
 * hands back a working client, or fails loudly, by name, before anything
 * downstream gets a chance to guess why a query returned nothing.
 */
export const supabase = createClient(rawUrl, rawAnonKey)
